import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Download,
  RefreshCw,
  BarChart3,
  DollarSign,
  Mail,
  TestTube,
} from 'lucide-react';
import {
  getDashboardAnalytics,
  exportOverviewToExcel,
  DashboardAnalytics,
} from '@/services/analyticsService';

export const AnalyticsOverviewPage: React.FC = () => {
  const [data, setData] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDashboardAnalytics();
      setData(response);
    } catch (err) {
      setError('Erreur lors du chargement des analyses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportOverviewToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-overview-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchData}
            className="ml-auto px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vue d'ensemble</h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyse globale de la plateforme NEXEDU
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Export...' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Institutions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Etablissements</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {data?.overview.totalInstitutions || 0}
              </p>
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp size={14} />
                +{data?.overview.newInstitutionsThisMonth || 0} ce mois
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        {/* Users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Utilisateurs</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {data?.overview.totalUsers || 0}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {data?.overview.activeUsersLast30Days || 0} actifs (30j)
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">MRR</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatCurrency(data?.billing.mrr || 0)}
              </p>
              <p className={`text-sm mt-2 flex items-center gap-1 ${
                (data?.billing.revenueGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {(data?.billing.revenueGrowth || 0) >= 0 ? (
                  <TrendingUp size={14} />
                ) : (
                  <TrendingDown size={14} />
                )}
                {formatPercent(data?.billing.revenueGrowth || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>

        {/* Tests */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Tests</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatPercent(data?.tests.passRate || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {data?.tests.totalTests || 0} tests total
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TestTube className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Billing Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Facturation</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">ARR</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(data?.billing.arr || 0)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Abonnements</p>
              <p className="text-xl font-bold text-gray-900">
                {data?.billing.totalSubscriptions || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Taux de Churn</p>
              <p className={`text-xl font-bold ${
                (data?.billing.churnRate || 0) <= 5 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatPercent(data?.billing.churnRate || 0)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">NRR</p>
              <p className={`text-xl font-bold ${
                (data?.billing.nrr || 100) >= 100 ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {formatPercent(data?.billing.nrr || 100)}
              </p>
            </div>
          </div>
        </div>

        {/* Email Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Email Marketing</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Campagnes</p>
              <p className="text-xl font-bold text-gray-900">
                {data?.email.totalCampaigns || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Emails envoyés</p>
              <p className="text-xl font-bold text-gray-900">
                {data?.email.totalEmailsSent || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Taux d'ouverture</p>
              <p className="text-xl font-bold text-gray-900">
                {formatPercent(data?.email.openRate || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Accès rapide</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/admin/analytics/tests"
            className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <TestTube className="text-purple-600" size={20} />
            <span className="font-medium text-purple-900">Tests & Couverture</span>
          </a>
          <a
            href="/admin/analytics/users"
            className="flex items-center gap-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <Users className="text-green-600" size={20} />
            <span className="font-medium text-green-900">Utilisateurs</span>
          </a>
          <a
            href="/admin/analytics/billing"
            className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
          >
            <DollarSign className="text-yellow-600" size={20} />
            <span className="font-medium text-yellow-900">Facturation</span>
          </a>
          <a
            href="/admin/analytics/email"
            className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Mail className="text-blue-600" size={20} />
            <span className="font-medium text-blue-900">Email Marketing</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverviewPage;
